import { useState, useEffect } from "react";
import { showErrorPopup, translateError } from "@components/ErrorHandling";
import { getAppData } from "./storageFallback";

export const globalCampaignIdent = import.meta.env.VITE_APP_CAMPAIGN_IDENT

export function themeAvailable(theme) {
  const availableThemes = [
    "glo",
    "lucky-strike",
    "vuse",
    "pall-mall",
    "fair-play",
  ];

  return availableThemes.includes(theme) ? true : false;
}

export function useWindowSize() {
  const isClient = typeof window === "object";

  const [windowSize, setWindowSize] = useState({
    width: isClient ? window.innerWidth : undefined,
    height: isClient ? window.innerHeight : undefined,
  });

  useEffect(() => {
    const isClient = typeof window === "object";

    if (!isClient) {
      return false;
    }

    function handleResize() {
      setWindowSize({
        width: isClient ? window.innerWidth : undefined,
        height: isClient ? window.innerHeight : undefined,
      });
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
}

export function sendEvent(event, payload) {
  const data = {
    event: {
      ident: event,
      payload: payload || null,
    },
  };

  fetch(import.meta.env.VITE_API_URL + "/consumer/event-create", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization:
        "Bearer " + JSON.parse(getAppData("token"))?.uuid,
    },
    body: JSON.stringify(data),
  })
    .then((response) => {
      return response.json();
    })
    .then(
      (result) => { },
      (error) => {
        console.log(error);
      }
    );
}

export function leadingZero(day) {
  let d = parseInt(day);
  if (d < 10) {
    return "0" + d;
  } else {
    return "" + d;
  }
}

export function parseDate(d, hours, showSeconds, addDays) {
  let date;
  try {
    date = new Date(d.replace(" ", "T"));
  } catch (error) {
    return "n/d";
  }
  let seconds = ":" + leadingZero(date.getSeconds());

  if (showSeconds === false) {
    seconds = "";
  }

  if (addDays) {
    date.setDate(date.getDate() + addDays);
  }

  if (hours) {
    return (
      leadingZero(date.getDate()) +
      "/" +
      leadingZero(date.getMonth() + 1) +
      "/" +
      date.getFullYear() +
      ", " +
      leadingZero(date.getHours()) +
      ":" +
      leadingZero(date.getMinutes()) +
      seconds
    );
  } else {
    return (
      leadingZero(date.getDate()) +
      "." +
      leadingZero(date.getMonth() + 1) +
      "." +
      date.getFullYear()
    );
  }
}

export function localeString(string) {
  const date = new Date(string);

  return date.toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function debounce(func, timeout = 300) {
  let timer;
  return function (...args) {
    const context = this;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      func.apply(context, args);
    }, timeout);
  };
}

export function getModuleValue(modulesArray, type, field) {
  if (!modulesArray || modulesArray.length === 0) {
    return;
  }
  let fieldSplit = field.split(".");
  let data = modulesArray?.filter((obj) => obj.type === type)[0];
  for (let i = 0; i < fieldSplit.length; i++) {
    data = data?.[fieldSplit[i]];
  }
  return data;
}

export async function fetchData({
  endpoint,
  method = "POST",
  payload,
  successCallback,
  errorCallback = false,
  fetchErrorCallback = false,
  shouldThrowError = true,
  setLoading = false,
  controller = false,
  blob = false,
  token = false,
  headers = {}
} = {}) {
  let resObj;

  if (setLoading) {
    setLoading(true);
  }
  const response = await fetch(import.meta.env.VITE_API_URL + endpoint, {
    method: method,
    headers: token || JSON.parse(getAppData("sessionToken"))?.uuid ? {
      "Content-Type": "application/json",
      "X-app-id": import.meta.env.VITE_COMMIT_ID,
      // "X-app-bypass": "sandbox",
      Authorization: `Bearer ${token ? token : JSON.parse(getAppData("sessionToken"))?.uuid
        }`,
      ...headers
    } : {
      "Content-Type": "application/json",
      "X-app-id": import.meta.env.VITE_COMMIT_ID,
      ...headers
    },
    body: JSON.stringify(payload),
    ...(controller && { signal: controller.signal }),
  });

  if (blob) {
    resObj = await response;
  } else {
    resObj = await response.json();
  }

  if (resObj.status.success || resObj.status === 200) {
    if (successCallback) successCallback(resObj);
  } else {
    if (errorCallback) {
      errorCallback(resObj);
    }
    if (shouldThrowError) {
      showErrorPopup(
        translateError(resObj.data.error),
        resObj.meta.ts,
        resObj.data.details
      );
    } else if (resObj.data.error === "error_unauthorized") {
      showErrorPopup(
        translateError(resObj.data.error),
        resObj.meta.ts,
        resObj.data.details
      );
    }
    throw new Error("Fetch error");
  }

  if (setLoading) {
    setLoading(false);
  }

  if (blob) {
    try {
      return await resObj.blob();
    } catch (error) {
      throw new Error("Error while getting blob", error);
    }
  } else {
    return await resObj?.data;
  }
}

export function parseCMSHighlight(input, specialChar = "**") {
  let output = "";
  let insideSpanTag = false;
  let i = 0;

  while (i < input.length) {
    const isSpecialChar =
      input.slice(i, i + specialChar.length) === specialChar;

    if (isSpecialChar) {
      output += insideSpanTag ? "</span>" : "<span>";
      insideSpanTag = !insideSpanTag;
      i += specialChar.length;
    } else {
      output += input[i];
      i++;
    }
  }

  if (insideSpanTag) {
    output += "</span>";
  }

  return output;
}
